import { CommonModule } from "@angular/common";
import { Component } from "@angular/core";
import { ReactiveFormsModule, FormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatDialogModule, MatDialogRef } from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";

@Component({
  selector: 'app-reset-confirmation-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    FormsModule
  ],
  template: `
    <div class="reset-confirmation-dialog p-3">
      <!-- Header -->
      <div class="dialog-header">
        <div class="header-content">
          <div class="header-icon">
            <mat-icon>warning</mat-icon>
          </div>
          <div class="header-text">
            <h2>Complete HA System Reset</h2>
            <p>This action cannot be undone</p>
          </div>
        </div>
      </div>

      <!-- Content -->
      <div class="dialog-body p-2">
        <div class="warning-section">
          <h3>This operation will permanently:</h3>
          <div class="operation-list">
            <div class="operation-item">
              <mat-icon class="item-icon">stop_circle</mat-icon>
              <span>Stop all sync services on both hosts</span>
            </div>
            <div class="operation-item">
              <mat-icon class="item-icon">link_off</mat-icon>
              <span>Remove all linked servers</span>
            </div>
            <div class="operation-item">
              <mat-icon class="item-icon">toggle_off</mat-icon>
              <span>Disable HA triggers</span>
            </div>
            <div class="operation-item">
              <mat-icon class="item-icon">delete_sweep</mat-icon>
              <span>Clear HAConfig database entries</span>
            </div>
            <div class="operation-item">
              <mat-icon class="item-icon">settings_backup_restore</mat-icon>
              <span>Reset UseHA flag to false</span>
            </div>
          </div>
        </div>

        <div class="confirmation-section">
          <div class="danger-notice">
            <mat-icon>error</mat-icon>
            <strong>This cannot be undone!</strong>
          </div>
          
          <div class="input-section">
            <label class="input-label">Type <strong>"RESET"</strong> to confirm:</label>
            <mat-form-field appearance="outline" class="confirmation-field">
              <input matInput 
                     [(ngModel)]="confirmationText" 
                     (input)="onInputChange()"
                     placeholder="Type RESET here..."
                     maxlength="5"
                     class="confirmation-input"
                     autocomplete="off">
            </mat-form-field>
            
            <div class="error-message" *ngIf="showError">
              <mat-icon>info</mat-icon>
              Please type "RESET" exactly to proceed.
            </div>
          </div>
        </div>
      </div>

      <!-- Actions -->
      <div class="dialog-actions">
        <button mat-stroked-button 
                class="cancel-button"
                (click)="onCancel()">
          <mat-icon>close</mat-icon>
          Cancel
        </button>
        <button mat-raised-button 
                color="warn"
                class="confirm-button"
                [disabled]="!isConfirmationValid"
                (click)="onConfirm()">
          <mat-icon>delete_forever</mat-icon>
          Execute Reset
        </button>
      </div>
    </div>
  `,
  styles: [`
    .reset-confirmation-dialog {
      width: 520px;
      max-width: 90vw;
      max-height: 80vh;
      display: flex;
      flex-direction: column;
    }

    /* Header Styles */
    .dialog-header {
      padding: 0;
      margin: -24px -24px 0 -24px;
      border-radius: 4px 4px 0 0;
      border-bottom: 2px solid #e0e0e0;
    }

    .header-content {
      display: flex;
      align-items: center;
      padding: 20px 24px;
      gap: 16px;
    }

    .header-icon {
      flex-shrink: 0;
      
      mat-icon {
        font-size: 32px;
        width: 32px;
        height: 32px;
        color: #ffeb3b;
      }
    }

    .header-text {
      flex: 1;
      
      h2 {
        margin: 0 0 4px 0;
        font-size: 20px;
        font-weight: 600;
        line-height: 1.2;
      }
      
      p {
        margin: 0;
        font-size: 14px;
        opacity: 0.9;
        font-weight: 400;
      }
    }

    /* Body Styles */
    .dialog-body {
      padding: 24px 0;
      flex: 1;
      overflow-y: auto;
    }

    .warning-section {
      margin-bottom: 24px;
      
      h3 {
        margin: 0 0 16px 0;
        font-size: 16px;
        font-weight: 500;
        color: #333;
      }
    }

    .operation-list {
      display: flex;
      flex-direction: column;
      gap: 12px;
    }

    .operation-item {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 8px 0;
      
      .item-icon {
        font-size: 20px;
        width: 20px;
        height: 20px;
        color: #ff5722;
        flex-shrink: 0;
      }
      
      span {
        font-size: 14px;
        color: #555;
        line-height: 1.4;
      }
    }

    .confirmation-section {
      border-top: 1px solid #e0e0e0;
      padding-top: 20px;
    }

    .danger-notice {
      display: flex;
      align-items: center;
      gap: 8px;
      background: linear-gradient(45deg, #fff3e0 0%, #fce4ec 100%);
      border: 1px solid #ff9800;
      border-radius: 6px;
      padding: 12px 16px;
      margin-bottom: 20px;
      
      mat-icon {
        font-size: 20px;
        width: 20px;
        height: 20px;
        color: #f44336;
      }
      
      strong {
        color: #d84315;
        font-size: 14px;
      }
    }

    .input-section {
      .input-label {
        display: block;
        font-size: 14px;
        font-weight: 500;
        color: #333;
        margin-bottom: 12px;
        
        strong {
          color: #f44336;
          font-weight: 700;
        }
      }
    }

    .confirmation-field {
      width: 100%;
      margin-bottom: 8px;
      
      ::ng-deep {
        .mat-mdc-form-field-outline {
          color: #ddd;
        }
        
        .mat-mdc-form-field-focus-overlay {
          background-color: #f44336;
        }
        
        &.mat-focused .mat-mdc-form-field-outline-thick {
          color: #f44336;
        }
      }
    }

    .confirmation-input {
      font-family: 'Courier New', monospace;
      font-weight: 700;
      font-size: 16px;
      text-transform: uppercase;
      letter-spacing: 2px;
      text-align: center;
    }

    .error-message {
      display: flex;
      align-items: center;
      gap: 6px;
      color: #f44336;
      font-size: 12px;
      font-weight: 500;
      
      mat-icon {
        font-size: 16px;
        width: 16px;
        height: 16px;
      }
    }

    /* Actions Styles */
    .dialog-actions {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 20px 0 0 0;
      border-top: 1px solid #e0e0e0;
      gap: 12px;
      margin-top: auto;
    }

    .cancel-button,
    .confirm-button {
      display: flex;
      align-items: center;
      gap: 8px;
      min-width: 120px;
      height: 40px;
      font-weight: 500;
      
      mat-icon {
        font-size: 18px;
        width: 18px;
        height: 18px;
      }
    }

    .cancel-button {
      color: #666;
      border-color: #ddd;
      
      &:hover {
        background-color: #f5f5f5;
        border-color: #bbb;
      }
    }

    .confirm-button {
      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
      
      &:not(:disabled):hover {
        background-color: #d32f2f;
      }
    }

    /* Responsive Design */
    @media (max-width: 600px) {
      .reset-confirmation-dialog {
        width: 100%;
        max-width: 100vw;
        margin: 0;
      }
      
      .header-content {
        padding: 16px 20px;
        gap: 12px;
      }
      
      .header-icon mat-icon {
        font-size: 28px;
        width: 28px;
        height: 28px;
      }
      
      .header-text h2 {
        font-size: 18px;
      }
      
      .dialog-body {
        padding: 20px 0;
      }
      
      .dialog-actions {
        flex-direction: column-reverse;
        gap: 8px;
        
        .cancel-button,
        .confirm-button {
          width: 100%;
          min-width: auto;
        }
      }
    }

    /* Animation for smooth appearance */
    .reset-confirmation-dialog {
      animation: slideIn 0.3s ease-out;
    }

    @keyframes slideIn {
      from {
        opacity: 0;
        transform: translateY(-20px) scale(0.95);
      }
      to {
        opacity: 1;
        transform: translateY(0) scale(1);
      }
    }
  `]
})
export class ResetConfirmationDialogComponent {
  confirmationText: string = '';
  showError: boolean = false;

  constructor(
    private dialogRef: MatDialogRef<ResetConfirmationDialogComponent>
  ) {}

  get isConfirmationValid(): boolean {
    return this.confirmationText === 'RESET';
  }

  onInputChange() {
    this.showError = this.confirmationText.length > 0 && !this.isConfirmationValid;
  }

  onCancel() {
    this.dialogRef.close();
  }

  onConfirm() {
    if (this.isConfirmationValid) {
      this.dialogRef.close('confirmed');
    } else {
      this.showError = true;
    }
  }
}